import * as React from "react";

import Form from "@ant-design/compatible/es/form";
import { DownOutlined } from "@ant-design/icons";
import { formatUserName } from "@impero-com/common/utils/formatting";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { toSet } from "@impero-com/common/utils/set";
import { Button, InfoTooltip, Svg } from "@impero-com/ui";
import RemoveIcon from "@impero-com/ui/img/remove.svg";
import { Dropdown, Menu, Table } from "antd";
import { SortOrder } from "antd/es/table/interface";
import { ItemType } from "rc-menu/es/interface";

import { UserOverview, StandardAccessRight } from "api/generated/model";
import {
    ChangeError,
    EditFormProps,
    EditProps,
    EditState,
    UNLOCKED,
    ValueLock,
} from "components/form/legacyFields";
import { StringInput } from "components/form/legacyFields/stringInput";
import { UserTypeahead } from "components/userTypeahead";
import { formatAccessRight } from "utils/accessRights";
import { ACCESS_RIGHTS } from "utils/accessRights/constants";
import {
    AccessRightsVariants,
    ResourceAccessRights,
    UserAndRights,
} from "utils/accessRights/models";
import * as F from "utils/form";

import { getLocalizedAccessRightDescription } from "./localized";
import * as theme from "./theme.css";

const FormItem = Form.Item;

interface AccessRightsValue<RAR extends ResourceAccessRights> {
    responsible: UserOverview | undefined;
    usersAndRights: UserAndRights<RAR>[];
}

interface CreateAccessRightsProps<
    ID extends string,
    R,
    RAR extends ResourceAccessRights,
> extends F.FormComponentProps<ID, AccessRightsValue<RAR>, R> {
    id: ID;
    initialValue?: AccessRightsValue<RAR>;
    validators?: F.ValidationRule<AccessRightsValue<RAR>>[];
    resourceAccessRights: RAR;

    // Events
    onSetResponsible?: (user: UserOverview) => void;
    onAddUser?: (userAndRight: UserAndRights<RAR>) => void;
    onChangedAccessRight?: (
        userId: number,
        accessRight: StandardAccessRight
    ) => void;
    onRemoveUser?: (userId: number) => void;
}

class CreateAccessRights<
    ID extends string,
    R,
    RAR extends ResourceAccessRights,
> extends React.PureComponent<CreateAccessRightsProps<ID, R, RAR>> {
    render = () => {
        const { form, initialValue, id, resourceAccessRights } = this.props;
        const { onAddUser, onChangedAccessRight, onRemoveUser } = this.props;
        const responsible = initialValue?.responsible;
        const usersAndRights = initialValue?.usersAndRights;

        const childrenForm = F.subForm(id, form);
        const { getFieldDecorator } = childrenForm;

        return (
            <>
                <div className={theme.responsible}>
                    <div className={theme.accessRightLabel}>
                        {t("AccessRights_Responsible")}
                    </div>
                    <FormItem>
                        {getFieldDecorator("responsible", {
                            rules: [{ validator: this.checkResponsibleIsSet }],
                            initialValue: responsible,
                            preserve: true,
                        })(
                            <UserTypeahead
                                id="responsible"
                                onSelect={this.setResponsible}
                                placeholder={t("AccessRights_SetResponsible")}
                            />
                        )}
                    </FormItem>
                </div>
                <CreateSimpleAccessRights
                    id="usersAndRights"
                    form={childrenForm}
                    initialValue={usersAndRights}
                    resourceAccessRights={resourceAccessRights}
                    onAddUser={onAddUser}
                    onChangedAccessRight={onChangedAccessRight}
                    onRemoveUser={onRemoveUser}
                />
            </>
        );
    };

    private checkResponsibleIsSet = (rule: any, value: any, callback: any) => {
        const {
            form: { getFieldValue },
            id,
        } = this.props;

        const responsible = getFieldValue(id)?.responsible;
        if (!responsible) {
            callback(t("AccessRights_SetResponsible_Validation"));
        }

        callback();
    };

    private setResponsible = (responsible: UserOverview) => {
        const { onSetResponsible, form, id } = this.props;
        const childrenForm = F.subForm(id, form);
        const { setFields, validateFields } = childrenForm;

        setFields(
            F.casterFieldWithIdWrapper<UserOverview | undefined>()(
                "responsible",
                childrenForm,
                {
                    value: responsible,
                    validating: true,
                }
            ),
            () => {
                validateFields(["responsible"]);
                if (onSetResponsible) {
                    onSetResponsible(responsible);
                }
            }
        );
    };
}

interface EditAccessRightsProps<
    RAR extends ResourceAccessRights,
> extends EditProps<AccessRightsValue<RAR>> {
    resourceAccessRights: RAR;

    onSetResponsible?: (user: UserOverview) => void;
    onAddUser?: (userAndRight: UserAndRights<RAR>) => void;
    onChangedAccessRight?: (
        userId: number,
        accessRight: StandardAccessRight
    ) => void;
    onRemoveUser?: (userId: number) => void;
}

interface EditAccessRightsForm<RAR extends ResourceAccessRights> {
    responsible?: F.Field<UserOverview | undefined>;
    usersAndRights: F.Field<UserAndRights<RAR>[]>;
}

// Unfortunate any typing. Antd's internals doesn't allow us to use type-safety as much as I'd like to.
// This is no big deal since EditAccessRightsForm is only to be used internally in this module,
// but the wrapper around it is safe.
const EditAccessRightsForm = F.FormCreate<
    { [K: string]: AccessRightsValue<any> },
    EditAccessRightsProps<any> &
        EditFormProps<AccessRightsValue<any>> &
        F.FormRootComponentProps<{ [K: string]: AccessRightsValue<any> }>,
    { [K: string]: EditAccessRightsForm<any> }
>({
    onFieldsChange(props, fields) {
        const field = fields[props.id];
        if (!field) {
            return;
        }
        const validating =
            field.responsible?.validating || field.usersAndRights?.validating;
        const errors: F.ObjectError = props.form.getFieldError(props.id);
        const hasError = F.hasError(errors);
        const value = props.form.getFieldValue(props.id);
        if (value === undefined) {
            throw new Error(
                `[${props.id}]: Value should not be undefined at this point`
            );
        }

        if (!validating && !hasError) {
            const promise = props.onChange(value);
            if (promise) {
                const { setPendingChanges, setChangeErrors } = props;
                setPendingChanges(true);
                promise
                    .then(() => {
                        setPendingChanges(false);
                    })
                    .catch((err) => {
                        setChangeErrors([
                            { message: err.toString(), field: props.id },
                        ]);
                    });
            } else {
                const { unlockValue } = props;
                unlockValue();
            }
        } else {
            const { lockValue } = props;
            let changeErrors: ChangeError[] | undefined;
            if (hasError) {
                changeErrors = [];
                if (Array.isArray(errors.responsible)) {
                    changeErrors = changeErrors.concat(
                        errors.responsible.map((message: string) => ({
                            message,
                            field: `${props.id}.responsible`,
                        }))
                    );
                }
                if (Array.isArray(errors.usersAndRights)) {
                    changeErrors = changeErrors.concat(
                        errors.usersAndRights.map((message: string) => ({
                            message,
                            field: `${props.id}.usersAndRights`,
                        }))
                    );
                }
            }
            lockValue(value, validating, changeErrors);
        }
    },
    mapPropsToFields(
        props: EditAccessRightsProps<any> &
            EditFormProps<AccessRightsValue<any>>
    ) {
        const lock: ValueLock<AccessRightsValue<any>> = props.lock;
        const value: AccessRightsValue<any> =
            lock.type === "Locked" ? lock.value : props.value;
        return {
            [`${props.id}.responsible`]: Form.createFormField({
                value: value.responsible,
                errors: F.pickFieldErrors(
                    `${props.id}.responsible`,
                    props.changeErrors
                ),
                validating: props.pendingChanges || undefined,
            }),
            [`${props.id}.usersAndRights`]: Form.createFormField({
                value: value.usersAndRights,
                errors: F.pickFieldErrors(
                    `${props.id}.usersAndRights`,
                    props.changeErrors
                ),
                validating: props.pendingChanges || undefined,
            }),
        };
    },
})(CreateAccessRights);

class EditAccessRights<
    RAR extends ResourceAccessRights,
> extends React.PureComponent<
    EditAccessRightsProps<RAR>,
    EditState<AccessRightsValue<RAR>>
> {
    mounted = false;
    state: EditState<AccessRightsValue<RAR>> = {
        pendingChanges: false,
        changeErrors: undefined,
        lock: UNLOCKED,
    };

    render = () => {
        return (
            <EditAccessRightsForm
                {...this.props}
                {...this.state}
                setPendingChanges={this.setPendingChanges}
                setChangeErrors={this.setChangeErrors}
                lockValue={this.lockValue}
                unlockValue={this.unlockValue}
            />
        );
    };

    setPendingChanges = (pendingChanges: boolean) => {
        if (!this.mounted) {
            return;
        }
        this.setState({
            pendingChanges,
            changeErrors: undefined,
            lock: pendingChanges ? this.state.lock : UNLOCKED,
        });
    };

    setChangeErrors = (changeErrors: ChangeError[]) => {
        if (!this.mounted) {
            return;
        }
        this.setState({
            pendingChanges: false,
            changeErrors,
        });
    };

    lockValue = (
        value: AccessRightsValue<RAR>,
        validating: boolean | undefined,
        error: ChangeError[] | undefined
    ) => {
        this.setState({
            lock: { type: "Locked", value },
            pendingChanges: validating ?? false,
            changeErrors: error,
        });
    };

    unlockValue = () => {
        if (this.state.lock.type === "Locked") {
            this.setState({
                lock: UNLOCKED,
                pendingChanges: false,
                changeErrors: undefined,
            });
        }
    };

    componentDidMount = () => {
        this.mounted = true;
    };

    componentWillUnmount = () => {
        this.mounted = false;
    };
}

class ReadOnlyAccessRights<
    RAR extends ResourceAccessRights,
> extends React.PureComponent<ReadOnlyAccessRightsProps<RAR>> {
    render = () => {
        const { value, resourceAccessRights } = this.props;
        return (
            <>
                <div className={theme.responsible}>
                    <StringInput
                        type="READ_ONLY"
                        label={t("AccessRights_Responsible")}
                        value={formatUserName(value.responsible)}
                    />
                </div>
                <ReadOnlySimpleAccessRights
                    value={value.usersAndRights}
                    resourceAccessRights={resourceAccessRights}
                />
            </>
        );
    };
}

interface ReadOnlyAccessRightsProps<RAR extends ResourceAccessRights> {
    value: AccessRightsValue<RAR>;
    resourceAccessRights: RAR;
}

type AccessRightsProps<
    ID extends string,
    R,
    RAR extends ResourceAccessRights,
> =
    | ({ type: "READ_ONLY" } & ReadOnlyAccessRightsProps<RAR>)
    | ({ type: "EDIT" } & EditAccessRightsProps<RAR>)
    | ({ type: "CREATE" } & CreateAccessRightsProps<ID, R, RAR>);

class AccessRights<
    ID extends string,
    R,
    RAR extends ResourceAccessRights,
> extends React.PureComponent<AccessRightsProps<ID, R, RAR>> {
    render = () => {
        switch (this.props.type) {
            case "READ_ONLY": {
                const { type: _type, ...rest } = this.props;
                return <ReadOnlyAccessRights {...rest} />;
            }
            case "EDIT": {
                const { type: _type, ...rest } = this.props;
                return <EditAccessRights {...rest} />;
            }
            case "CREATE": {
                const { type: _type, ...rest } = this.props;
                return <CreateAccessRights {...rest} />;
            }
            default:
                return assertNever(this.props);
        }
    };
}

interface CreateSimpleAccessRightsProps<
    ID extends string,
    R,
    RAR extends ResourceAccessRights,
> extends F.FormComponentProps<ID, UserAndRights<RAR>[], R> {
    id: ID;
    initialValue?: UserAndRights<RAR>[];
    validators?: F.ValidationRule<UserAndRights<RAR>[]>[];
    resourceAccessRights: RAR;

    // Events
    onAddUser?: (userAndRight: UserAndRights<RAR>) => void;
    onChangedAccessRight?: (
        userId: number,
        accessRight: StandardAccessRight
    ) => void;
    onRemoveUser?: (userId: number) => void;
}

interface SimpleAccessRightsState<RAR extends ResourceAccessRights> {
    accessRightForNewUser: AccessRightsVariants<RAR>;
    lastUserAddedId: number | null;
    sortedInfo: SortedInfo;
}

class CreateSimpleAccessRights<
    ID extends string,
    R,
    RAR extends ResourceAccessRights,
> extends React.PureComponent<
    CreateSimpleAccessRightsProps<ID, R, RAR>,
    SimpleAccessRightsState<RAR>
> {
    state: SimpleAccessRightsState<RAR> = {
        accessRightForNewUser:
            this.props.resourceAccessRights.defaultAccessRight,
        sortedInfo: {
            order: "ascend",
            columnKey: "accessRight",
        },
        lastUserAddedId: null,
    };

    render = () => {
        const { resourceAccessRights, form, initialValue, id, validators } =
            this.props;
        const { getFieldDecorator } = form;
        const { lastUserAddedId } = this.state;

        const adminValidator = [];
        if (resourceAccessRights.requiresAdmin) {
            adminValidator.push({
                validator: this.checkThereIsOneUserWithAdminAccessRight,
            });
        }

        return (
            <div className={theme.table}>
                <div className={theme.accessRightLabel}>
                    {t("AccessRights_AccessManagement")}
                </div>
                <FormItem>
                    {getFieldDecorator(id, {
                        rules: [...adminValidator, ...(validators ?? [])],
                        trigger: "", // All triggers are handled by direct calls to setFieldsValue.
                        validateFirst: true,
                        initialValue: F.castValue(id, form, initialValue),
                        getValueProps: (value) => {
                            return { dataSource: value };
                        },
                        preserve: true,
                    })(
                        <Table
                            summary={this.footerToAddUser()}
                            columns={this.getColumns()}
                            rowKey={(record: UserAndRights<RAR>) =>
                                record.user.id.toString()
                            }
                            pagination={false}
                            locale={{
                                filterConfirm: t("AccessRights_Filter_Ok"),
                                filterReset: t("AccessRights_Filter_Reset"),
                            }}
                            rowClassName={(
                                userAndRights: UserAndRights<RAR>
                            ) =>
                                userAndRights.user.id === lastUserAddedId
                                    ? theme.recentlyAddedUser
                                    : ""
                            }
                            onChange={(_pagination, _filters, sorter) => {
                                if (Array.isArray(sorter)) {
                                    throw new Error(
                                        "Unexpected type of sorter Array"
                                    );
                                }
                                this.setState({
                                    sortedInfo: sorter,
                                });
                                this.preventNewUserToBeHighlightedMoreThanOnce();
                            }}
                            bordered
                        />
                    )}
                </FormItem>
            </div>
        );
    };

    private preventNewUserToBeHighlightedMoreThanOnce = () => {
        this.setState({
            lastUserAddedId: null,
        });
    };

    private footerToAddUser = () => {
        const {
            form: { getFieldValue },
            id,
        } = this.props;
        const { accessRightForNewUser } = this.state;
        const userIdsExcludedAutoCompletion = toSet(
            getFieldValue(id) ?? [],
            (userAndRights) => userAndRights.user.id.toString()
        );

        return () => {
            return (
                <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>
                        <UserTypeahead
                            id="access-rights-table"
                            value={undefined}
                            onSelect={this.addUser}
                            placeholder={t("AccessRights_AddNew")}
                            filter={(user) =>
                                userIdsExcludedAutoCompletion[user.id] ===
                                undefined
                            }
                        />
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1}>
                        <Dropdown
                            overlay={this.accessRightsMenuToAddNewUser()}
                            trigger={["click"]}
                            placement="bottom"
                        >
                            <a className="ant-dropdown-link" href="#">
                                {formatAccessRight(accessRightForNewUser)}{" "}
                                <DownOutlined />
                            </a>
                        </Dropdown>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} />
                </Table.Summary.Row>
            );
        };
    };

    private getColumns = () => {
        const { resourceAccessRights } = this.props;
        const { sortedInfo } = this.state;

        const userColumn = {
            title: t("AccessRights_UserColum"),
            key: "userId",
            render: (_text: string, userAndRights: UserAndRights<RAR>) => (
                <>
                    <div className={theme.user}>
                        {formatUserName(userAndRights.user)}
                    </div>
                    <div className={theme.email}>
                        {userAndRights.user.email}
                    </div>
                </>
            ),
            className: theme.header,
            sortOrder:
                sortedInfo.columnKey === "userId" ? sortedInfo.order : null,
            sorter: (a: UserAndRights<RAR>, b: UserAndRights<RAR>) =>
                formatUserName(a.user).localeCompare(formatUserName(b.user)) ||
                a.accessRight.localeCompare(b.accessRight),
            width: "80%",
        };

        const accessRightsColumn = {
            title: t("AccessRights_AccessRightsColumn"),
            key: "accessRight",
            render: (_text: string, userAndRights: UserAndRights<RAR>) => {
                const localizedAccessRight = formatAccessRight(
                    userAndRights.accessRight
                );

                return (
                    <Dropdown
                        overlay={this.accessRightsMenu(userAndRights.user.id)}
                        trigger={["click"]}
                        placement="bottom"
                    >
                        <a className="ant-dropdown-link" href="#">
                            {localizedAccessRight} <DownOutlined />
                        </a>
                    </Dropdown>
                );
            },
            className: theme.accessRightsHeader,
            filters: resourceAccessRights.accessRights.map(
                mapAccessRightToColumnFilter
            ),
            filterMultiple: false,
            onFilter: (
                value: string | number | boolean,
                record: UserAndRights<RAR>
            ) => record.accessRight.indexOf(String(value)) === 0,
            sorter: (a: UserAndRights<RAR>, b: UserAndRights<RAR>) =>
                a.accessRight.localeCompare(b.accessRight) ||
                formatUserName(a.user).localeCompare(formatUserName(b.user)),
            width: "15%",
            sortOrder:
                sortedInfo.columnKey === "accessRight"
                    ? sortedInfo.order
                    : null,
        };

        const deleteColumn = {
            width: "5%",
            render: (_text: string, userAndRights: UserAndRights<RAR>) => (
                <Button
                    id={"remove-button-" + userAndRights.user.id.toString()}
                    variant="link"
                    className={theme.removeButton}
                    onClick={() => this.removeUser(userAndRights.user.id)}
                >
                    <Svg className={theme.removeIcon} src={RemoveIcon} />
                </Button>
            ),
        };

        return [userColumn, accessRightsColumn, deleteColumn];
    };

    private checkThereIsOneUserWithAdminAccessRight = (
        _rule: any,
        _value: any,
        callback: any
    ) => {
        const {
            form: { getFieldValue },
            id,
        } = this.props;
        const thereIsUserWithAdminAccessRight = getFieldValue(id)?.some(
            (userAndRights: UserAndRights<RAR>) =>
                userAndRights.accessRight === ACCESS_RIGHTS.ADMIN
        );
        if (thereIsUserWithAdminAccessRight) {
            callback();
        } else {
            callback(t("AccessRights_User_WithAdminAccess_Validation"));
        }
    };

    // NOTE: The inner implementation of antd's validateFields has a different behaviour
    // depending on whether there are validators or not.
    // To avoid problems, we dont mark the field as "validating" if we have no validators
    // (note that we have a validator if requiresAdmin === true)
    private shouldValidateData = (): boolean =>
        (this.props.validators?.length ?? 0) > 0 ||
        this.props.resourceAccessRights.requiresAdmin;

    private addUser = (user: UserOverview) => {
        const { onAddUser, form, id } = this.props;
        const { getFieldValue, setFields, validateFields } = form;
        const { accessRightForNewUser } = this.state;
        const userToAdd = {
            user,
            accessRight: accessRightForNewUser,
        };

        const usersAndRights = getFieldValue(id) ?? [];
        setFields(
            F.casterFieldWithIdWrapper<UserAndRights<RAR>[]>()(id, form, {
                value: [...usersAndRights, userToAdd],
                validating: this.shouldValidateData(),
            }),
            () => {
                validateFields([id]);
                if (onAddUser) {
                    onAddUser(userToAdd);
                }
            }
        );

        this.setState({
            lastUserAddedId: user.id,
        });
    };

    private accessRightsMenuToAddNewUser = () => {
        const { resourceAccessRights } = this.props;
        const items = resourceAccessRights.accessRights.flatMap(
            (accessRight, index) =>
                mapAccessRightToMenuItem(
                    resourceAccessRights,
                    accessRight,
                    index
                )
        );

        return (
            <Menu
                className={theme.menu}
                items={items}
                onClick={(menuItem) => {
                    this.setState({
                        accessRightForNewUser:
                            menuItem.key as StandardAccessRight,
                    });
                }}
            />
        );
    };

    private accessRightsMenu = (userId: number) => {
        const { onChangedAccessRight, form, id, resourceAccessRights } =
            this.props;
        const { getFieldValue, setFields, validateFields } = form;
        const items = resourceAccessRights.accessRights.flatMap(
            (accessRight, index) =>
                mapAccessRightToMenuItem(
                    resourceAccessRights,
                    accessRight,
                    index
                )
        );

        return (
            <Menu
                className={theme.menu}
                items={items}
                onClick={(menuItem) => {
                    const accessRight = menuItem.key as StandardAccessRight;
                    const usersAndRights =
                        getFieldValue(id)?.map((userAndRight) =>
                            userAndRight.user.id === userId
                                ? { ...userAndRight, accessRight }
                                : userAndRight
                        ) ?? [];
                    setFields(
                        F.casterFieldWithIdWrapper<UserAndRights<RAR>[]>()(
                            id,
                            form,
                            {
                                value: usersAndRights,
                                validating: this.shouldValidateData(),
                            }
                        ),
                        () => {
                            validateFields([id]);
                            if (onChangedAccessRight) {
                                onChangedAccessRight(userId, accessRight);
                            }
                        }
                    );
                    this.preventNewUserToBeHighlightedMoreThanOnce();
                }}
            />
        );
    };

    private removeUser = (userId: number) => {
        const { onRemoveUser, form, id } = this.props;
        const { getFieldValue, setFields, validateFields } = form;

        const usersAndRights =
            getFieldValue(id)?.filter(
                (userAndRight) => userAndRight.user.id !== userId
            ) ?? [];
        setFields(
            F.casterFieldWithIdWrapper<UserAndRights<RAR>[]>()(id, form, {
                value: usersAndRights,
                validating: this.shouldValidateData(),
            }),
            () => {
                validateFields([id]);
                if (onRemoveUser) {
                    onRemoveUser(userId);
                }
            }
        );
        this.preventNewUserToBeHighlightedMoreThanOnce();
    };
}

interface EditSimpleAccessRightsProps<
    RAR extends ResourceAccessRights,
> extends EditProps<UserAndRights<RAR>[]> {
    resourceAccessRights: RAR;

    onAddUser?: (userAndRight: UserAndRights<RAR>) => void;
    onChangedAccessRight?: (
        userId: number,
        accessRight: StandardAccessRight
    ) => void;
    onRemoveUser?: (userId: number) => void;
}

// Unfortunate any typing. Antd's internals doesn't allow us to use type-safety as much as I'd like to.
// This is no big deal since EditSimpleAccessRightsForm is only to be used internally in this module,
// but the wrapper around it is safe.
const EditSimpleAccessRightsForm = F.FormCreate<
    { [K: string]: UserAndRights<any>[] },
    EditSimpleAccessRightsProps<any> &
        EditFormProps<UserAndRights<any>[]> &
        F.FormRootComponentProps<{ [K: string]: UserAndRights<any>[] }>
>({
    onFieldsChange(props, fields) {
        const field = fields[props.id];
        if (!field) {
            return;
        }
        if (!field.validating && !field.errors) {
            const promise = props.onChange(field.value);
            if (promise) {
                const { setPendingChanges, setChangeErrors } = props;
                setPendingChanges(true);
                promise
                    .then(() => {
                        setPendingChanges(false);
                    })
                    .catch((err) => {
                        setChangeErrors([
                            { message: err.toString(), field: props.id },
                        ]);
                    });
            } else {
                const { unlockValue } = props;
                unlockValue();
            }
        } else {
            const { lockValue } = props;
            lockValue(field.value, field.validating, field.errors);
        }
    },
    mapPropsToFields(props) {
        const lock: ValueLock<UserAndRights<any>[]> = props.lock;
        const value: UserAndRights<any>[] =
            lock.type === "Locked" ? lock.value : props.value;

        return {
            [props.id]: Form.createFormField({
                value,
                errors: props.changeErrors,
                validating: props.pendingChanges || undefined,
            }),
        };
    },
})(CreateSimpleAccessRights);

class EditSimpleAccessRights<
    RAR extends ResourceAccessRights,
> extends React.PureComponent<
    EditSimpleAccessRightsProps<RAR>,
    EditState<UserAndRights<RAR>[]>
> {
    mounted = false;
    state: EditState<UserAndRights<RAR>[]> = {
        pendingChanges: false,
        changeErrors: undefined,
        lock: UNLOCKED,
    };

    render = () => {
        return (
            <EditSimpleAccessRightsForm
                {...this.props}
                {...this.state}
                setPendingChanges={this.setPendingChanges}
                setChangeErrors={this.setChangeErrors}
                lockValue={this.lockValue}
                unlockValue={this.unlockValue}
            />
        );
    };

    setPendingChanges = (pendingChanges: boolean) => {
        if (!this.mounted) {
            return;
        }
        this.setState({
            pendingChanges,
            changeErrors: undefined,
            lock: pendingChanges ? this.state.lock : UNLOCKED,
        });
    };

    setChangeErrors = (changeErrors: ChangeError[]) => {
        if (!this.mounted) {
            return;
        }
        this.setState({
            pendingChanges: false,
            changeErrors,
        });
    };

    lockValue = (
        value: UserAndRights<RAR>[],
        validating: boolean | undefined,
        error: ChangeError[] | undefined
    ) => {
        this.setState({
            lock: { type: "Locked", value },
            pendingChanges: validating ?? false,
            changeErrors: error,
        });
    };

    unlockValue = () => {
        if (this.state.lock.type === "Locked") {
            this.setState({
                lock: UNLOCKED,
                pendingChanges: false,
                changeErrors: undefined,
            });
        }
    };

    componentDidMount = () => {
        this.mounted = true;
    };

    componentWillUnmount = () => {
        this.mounted = false;
    };
}

class ReadOnlySimpleAccessRights<
    RAR extends ResourceAccessRights,
> extends React.PureComponent<
    ReadOnlySimpleAccessRightsProps<RAR>,
    ReadOnlySimpleAccessRightsState
> {
    state: ReadOnlySimpleAccessRightsState = {
        sortedInfo: {
            order: "ascend",
            columnKey: "accessRight",
        },
    };

    render = () => {
        const { value } = this.props;

        return (
            <div className={theme.table}>
                <div className={theme.accessRightLabel}>
                    {t("AccessRights_AccessManagement")}
                </div>
                <FormItem>
                    <Table
                        dataSource={value}
                        columns={this.getColumns()}
                        rowKey={(record: UserAndRights<RAR>) =>
                            record.user.id.toString()
                        }
                        pagination={false}
                        locale={{
                            filterConfirm: t("AccessRights_Filter_Ok"),
                            filterReset: t("AccessRights_Filter_Reset"),
                        }}
                        onChange={(_pagination, _filters, sorter) => {
                            if (Array.isArray(sorter)) {
                                throw new Error(
                                    "Unexpected type of sorter Array"
                                );
                            }
                            this.setState({
                                sortedInfo: sorter,
                            });
                        }}
                        bordered
                    />
                </FormItem>
            </div>
        );
    };

    private getColumns = () => {
        const { sortedInfo } = this.state;
        const { resourceAccessRights } = this.props;

        const userColumn = {
            title: t("AccessRights_UserColum"),
            key: "userId",
            render: (_text: string, userAndRights: UserAndRights<RAR>) => (
                <>
                    <div className={theme.user}>
                        {formatUserName(userAndRights.user)}
                    </div>
                    <div className={theme.email}>
                        {userAndRights.user.email}
                    </div>
                </>
            ),
            className: theme.header,
            sortOrder:
                sortedInfo.columnKey === "userId" ? sortedInfo.order : null,
            sorter: (a: UserAndRights<RAR>, b: UserAndRights<RAR>) =>
                formatUserName(a.user).localeCompare(formatUserName(b.user)) ||
                a.accessRight.localeCompare(b.accessRight),
            width: "85%",
        };

        const accessRightsColumn = {
            title: t("AccessRights_AccessRightsColumn"),
            key: "accessRight",
            render: (_text: string, userAndRights: UserAndRights<RAR>) => {
                return (
                    <div>
                        {formatAccessRight(userAndRights.accessRight)}
                        <span className={theme.accessRightTooltipContainer}>
                            <InfoTooltip
                                text={getLocalizedAccessRightDescription(
                                    resourceAccessRights,
                                    userAndRights.accessRight
                                )}
                            />
                        </span>
                    </div>
                );
            },
            className: theme.accessRightsHeader,
            filters: resourceAccessRights.accessRights.map(
                mapAccessRightToColumnFilter
            ),
            filterMultiple: false,
            onFilter: (
                value: string | number | boolean,
                record: UserAndRights<RAR>
            ) => record.accessRight.indexOf(String(value)) === 0,
            sorter: (a: UserAndRights<RAR>, b: UserAndRights<RAR>) =>
                a.accessRight.localeCompare(b.accessRight) ||
                formatUserName(a.user).localeCompare(formatUserName(b.user)),
            width: "15%",
            sortOrder:
                sortedInfo.columnKey === "accessRight"
                    ? sortedInfo.order
                    : null,
        };

        return [userColumn, accessRightsColumn];
    };
}

interface ReadOnlySimpleAccessRightsProps<RAR extends ResourceAccessRights> {
    value: UserAndRights<RAR>[];
    resourceAccessRights: RAR;
}

interface ReadOnlySimpleAccessRightsState {
    sortedInfo: SortedInfo;
}

type SimpleAccessRightsProps<
    ID extends string,
    R,
    RAR extends ResourceAccessRights,
> =
    | ({ type: "READ_ONLY" } & ReadOnlySimpleAccessRightsProps<RAR>)
    | ({ type: "EDIT" } & EditSimpleAccessRightsProps<RAR>)
    | ({ type: "CREATE" } & CreateSimpleAccessRightsProps<ID, R, RAR>);

class SimpleAccessRights<
    ID extends string,
    R,
    RAR extends ResourceAccessRights,
> extends React.PureComponent<SimpleAccessRightsProps<ID, R, RAR>> {
    render = () => {
        switch (this.props.type) {
            case "READ_ONLY": {
                const { type: _type, ...rest } = this.props;
                return <ReadOnlySimpleAccessRights {...rest} />;
            }
            case "EDIT": {
                const { type: _type, ...rest } = this.props;
                return <EditSimpleAccessRights {...rest} />;
            }
            case "CREATE": {
                const { type: _type, ...rest } = this.props;
                return <CreateSimpleAccessRights {...rest} />;
            }
            default:
                return assertNever(this.props);
        }
    };
}

const mapAccessRightToColumnFilter = (
    accessRight: StandardAccessRight
): { text: string; value: string } => {
    return { text: formatAccessRight(accessRight), value: accessRight };
};

const mapAccessRightToMenuItem = (
    accessRights: ResourceAccessRights,
    accessRight: StandardAccessRight,
    index: number
): ItemType[] => {
    const menuItemLabel = (
        <>
            <div className={theme.menuItemTitle}>
                {formatAccessRight(accessRight)}
            </div>
            <div className={theme.menuItemDescription}>
                {getLocalizedAccessRightDescription(accessRights, accessRight)}
            </div>
        </>
    );

    const menuItem = {
        key: accessRight,
        label: menuItemLabel,
        className: theme.menuItem,
    };

    if (index > 0) {
        const dividerItem = {
            type: "divider" as const,
            key: index.toString(),
            className: theme.menuItemDivider,
        };
        return [dividerItem, menuItem];
    } else {
        return [menuItem];
    }
};

interface SortedInfo {
    order?: SortOrder;
    columnKey?: React.Key;
}
export type { SimpleAccessRightsProps, AccessRightsProps, AccessRightsValue };
export { SimpleAccessRights, AccessRights };
